import { useCallback, useState } from "react";

import {
  runBulkDelete,
  selectionAfter,
  selectedInView,
  type BulkDeleteResult,
  type BulkItem,
} from "./bulk-delete";
import type { DeleteOutcome } from "../use-entity-writes";

/**
 * Where a multi-select delete is: not started, asking, deleting, or done with
 * a result to read.
 */
export type BulkPhase =
  | { name: "idle" }
  | { name: "confirming"; items: BulkItem[] }
  | { name: "running"; items: BulkItem[]; done: number }
  | { name: "done"; result: BulkDeleteResult };

/**
 * The select mode of a list and the delete behind it.
 *
 * The selection is a set of ids that outlives search and reload; what is
 * deleted is only the selected rows still in the list being shown, which the
 * confirmation names, so a row hidden by a search is never deleted unseen.
 * `deleteOne` is the same delete the row's own button uses. After a run the
 * rows that went are dropped from the selection and the ones that failed stay
 * in it, and `onDeleted` fires once if anything went, so the list reloads
 * once.
 */
export function useBulkDelete({
  deleteOne,
  onDeleted,
}: {
  deleteOne: (id: string) => Promise<DeleteOutcome>;
  onDeleted: () => void;
}) {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [phase, setPhase] = useState<BulkPhase>({ name: "idle" });

  const enter = useCallback(() => setSelecting(true), []);
  const exit = useCallback(() => {
    setSelecting(false);
    setSelected(new Set());
    setPhase({ name: "idle" });
  }, []);

  const toggle = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  /** Selects every row shown, or clears them when they are all selected. */
  const toggleAll = useCallback((visible: readonly BulkItem[]) => {
    setSelected((current) => {
      const allSelected =
        visible.length > 0 && visible.every((item) => current.has(item.id));
      const next = new Set(current);
      for (const item of visible) {
        if (allSelected) next.delete(item.id);
        else next.add(item.id);
      }
      return next;
    });
  }, []);

  const ask = useCallback(
    (visible: readonly BulkItem[]) => {
      const items = selectedInView(selected, visible);
      if (items.length > 0) setPhase({ name: "confirming", items });
    },
    [selected],
  );

  const cancel = useCallback(
    () =>
      setPhase((current) =>
        current.name === "running" ? current : { name: "idle" },
      ),
    [],
  );

  const confirm = useCallback(async () => {
    if (phase.name !== "confirming") return;
    const { items } = phase;
    setPhase({ name: "running", items, done: 0 });
    const result = await runBulkDelete(items, deleteOne, (done) =>
      setPhase({ name: "running", items, done }),
    );
    setSelected((current) => selectionAfter(current, result));
    setPhase({ name: "done", result });
    if (result.deleted.length > 0) onDeleted();
  }, [phase, deleteOne, onDeleted]);

  return {
    selecting,
    selected,
    phase,
    enter,
    exit,
    toggle,
    toggleAll,
    ask,
    cancel,
    confirm,
  };
}

export type BulkDelete = ReturnType<typeof useBulkDelete>;
